import { type ReactNode } from 'react';
import Tippy from '@tippyjs/react';
import { Question } from '@hungryroot/icons';
import styles from './Tooltip.module.css';
import 'tippy.js/dist/tippy.css';

interface Props {
  children?: ReactNode;
  testid?: string;
  content: ReactNode;
  placement?:
    | 'top'
    | 'top-end'
    | 'top-start'
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'right'
    | 'right-end'
    | 'right-start'
    | 'left'
    | 'left-end'
    | 'left-start';
  trigger?: string;
  interactive?: boolean;
  maxWidth?: number | string;
  className?: string;
  showOnCreate?: boolean;
  scrollDismiss?: boolean;
  dismissClickTarget?: string;
  dismissDelay?: number;
  hideOnClick?: boolean;
  containerClassName?: string;
  onShown?: () => void;
}

const defaultIcon = (
  <div className={styles.default}>
    <Question />
  </div>
);

export const scrollPlugin = {
  fn(instance: { hide: () => void }) {
    function onScroll() {
      instance.hide();
    }
    return {
      onShow() {
        window.addEventListener('scroll', onScroll, { once: true });
      },
      onHide() {
        window.removeEventListener('scroll', onScroll);
      },
      onDestroy() {
        window.removeEventListener('scroll', onScroll);
      },
    };
  },
};

export function Tooltip({
  children = defaultIcon,
  maxWidth = 200,
  placement = 'bottom-start',
  className = '',
  showOnCreate = false,
  scrollDismiss = true,
  dismissDelay = undefined,
  ...props
}: Props) {
  const {
    content,
    testid,
    trigger,
    interactive,
    hideOnClick,
    containerClassName,
    dismissClickTarget,
    onShown,
  } = props;

  return (
    <Tippy
      plugins={scrollDismiss ? [scrollPlugin] : undefined}
      className={`tippy-tooltip ${className}`}
      content={content}
      placement={placement}
      trigger={trigger}
      interactive={interactive}
      maxWidth={maxWidth}
      theme="custom"
      hideOnClick={hideOnClick}
      onClickOutside={
        dismissClickTarget
          ? (instance, event) => {
              event.target === document.querySelector(dismissClickTarget)
                ? instance.hide()
                : null;
            }
          : undefined
      }
      showOnCreate={showOnCreate}
      onShow={
        dismissDelay
          ? (instance) => {
              setTimeout(() => {
                instance.hide();
              }, dismissDelay);
            }
          : undefined
      }
      onShown={onShown}
    >
      <div
        data-testid={testid}
        className={`${styles.icon} ${containerClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </Tippy>
  );
}
